// Images tab: "Product Page" (square / thumb / cover dropzones with Remove)
// and "Screenshots" (multi upload, drag to reorder, remove).
import React, { useState, useEffect } from "react"
import Tabs from "aldoo-ra/Tabs"
import RadioGroup from "aldoo-ra/RadioGroup"
import { Lightbox } from "aldoo-ra/ImageGallery"
import Button from "aldoo-ra/Button"
import { Bars2Icon, TrashIcon } from "@heroicons/react/24/outline"
import { UploadLocalizedImage } from "../../../AdminShared/api"
import MediaField from "../../../AdminShared/MediaField"
import { firstUrl } from "../../../AdminShared/ui"
import { LanguageTag } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import { MediaDropzone } from "../shared/shared"
import { urljoin } from "../shared/useProductEditor"
import { noticeSentForReview } from "../shared/review-notice"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// each image's box keeps the shape of the image it takes, at a width that
// shows it without dwarfing the others
const IMAGES = [
  // the picture as it came, in its own aspect: shown wherever the box fits
  // the picture (the product page, the portrait card, a banner)
  { field: "original", label: "Original", size: null, width: 320 },
  { field: "square", label: "Square Thumb", size: "1024x1024", width: 320 },
  { field: "thumb", label: "Thumb", size: "450x600", width: 270 },
  { field: "cover", label: "Cover", size: "1920x1080", width: 480 },
]
const aspectOf = (size) => (size ? size.replace("x", " / ") : undefined)

const uploadErrorText = (t, data, label, fallbackRatio) => {
  if (data.error === "invalid size") return t("The %s is not the correct size", label)
  if (data.error === "invalid aspect ratio") return t("The %s aspect ratio must be %s", label, data.expected || fallbackRatio)
  return typeof data.error === "string" ? data.message || data.error : JSON.stringify(data.error)
}

export default function ImagesTab({ editor, shouldShowField, isProductEditor, setUploadError }) {
  const { pid, product, read, set, language, defaultLanguage, imageUrl, isSpecified, reload, mediaRoot, urls } = editor
  const t = useProductEditorTexts()
  // the language a picture is shown in: its own in the dialog's language, else the one it falls back to
  const shownLanguageOf = (field) => {
    const id = product?.[field]
    const prepared = urls?.[String(id && typeof id === "object" ? id._id : id || "")]
    if (!prepared || firstUrl(prepared[language])) return language
    if (firstUrl(prepared[defaultLanguage])) return defaultLanguage
    if (firstUrl(prepared.en)) return "en"
    return Object.keys(prepared).find((lang) => firstUrl(prepared[lang])) || language
  }
  const [subTab, setSubTab] = useState("product")
  const [uploading, setUploading] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)
  // the screenshot open full size (its index); the arrow keys walk the list
  const [previewIndex, setPreviewIndex] = useState(null)

  // AddMediaDialog crops and hands over a File: the endpoint stays the same
  const fieldUploaded = async (data) => {
    if (!data?.url) return
    // a new picture for a product on sale: back to review
    noticeSentForReview(data, t, editor)
    setUploading(true)
    try {
      // the API already stored the file and pointed the entry at it: reload for the responsive urls
      await reload()
    } finally {
      setUploading(false)
    }
  }

  const screenshotUploaded = (data) => {
    if (!data?.url) return setUploadError(t`Failed to upload screenshot`)
    set("screenshots", [...(read("screenshots") || []), data.url])
  }

  const removeScreenshot = (index) => set("screenshots", (read("screenshots") || []).filter((_, i) => i !== index))

  // the picture a card turns to under the pointer: { mode, screenshot, image }
  const hoverImage = read("hoverImage") || {}
  const hoverMode = hoverImage.mode || "random"
  const setHover = (patch) => set("hoverImage", { mode: hoverMode, screenshot: hoverImage.screenshot || "", image: hoverImage.image || "", ...patch })
  const hoverUploaded = (data) => {
    if (!data?.url) return setUploadError(t`Failed to upload the hover image`)
    setHover({ mode: "custom", image: data.url })
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return
    const updated = [...(read("screenshots") || [])]
    const [item] = updated.splice(draggedIndex, 1)
    updated.splice(index, 0, item)
    set("screenshots", updated)
    setDraggedIndex(index)
  }

  const screenshots = read("screenshots") || []
  const showScreenshots = shouldShowField("screenshots")

  useEffect(() => {
    if (previewIndex === null) return
    const onKey = (e) => {
      if (e.key === "ArrowRight") setPreviewIndex((i) => (i + 1) % screenshots.length)
      if (e.key === "ArrowLeft") setPreviewIndex((i) => (i - 1 + screenshots.length) % screenshots.length)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [previewIndex, screenshots.length])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Tabs variant="admin" size="small" value={subTab} onChange={setSubTab} tabs={[{ value: "product", label: t`Product Page` }, ...(showScreenshots ? [{ value: "screenshots", label: t`Screenshots` }, { value: "hover", label: t`Hover Image` }] : [])]} />
        {/* the help center's words on the open part: the page's pictures, the screenshots, the hover image */}
        <HelpHint helpKey={`vendor.product#${subTab === "product" ? "product-page" : subTab === "screenshots" ? "screenshots" : "hover-image"}`} />
      </div>

      {subTab === "product" && (
        <div className="flex flex-wrap items-start gap-6">
          {IMAGES.filter((i) => shouldShowField(i.field)).map(({ field, label, size, width }) => {
            const selected = imageUrl(product[field])
            const shownIn = selected ? shownLanguageOf(field) : language
            return (
              <div key={field} className="flex flex-col gap-2 max-w-full" style={{ width }} data-testid={`image-${field}`}>
                <MediaDropzone
                  selected={selected}
                  name={`${pid}-${field}`}
                  size={size}
                  aspect={aspectOf(size)}
                  disabled={!isProductEditor}
                  uploading={uploading}
                  upload={(file) => UploadLocalizedImage(file, product[field] ? { id: product[field], language } : { ownerModel: "Product", owner: pid, field, language })}
                  onUploaded={fieldUploaded}
                  formatError={(data) => uploadErrorText(t, data, t(label).toLowerCase(), field === "square" ? t`square` : t`correct`)}
                  prompt={
                    <div className="text-center">
                      <div className="text-lg font-semibold">{t(label)}</div>
                      <div className="text-xs text-admin_text_secondary">{size || t`any size, its own aspect`}</div>
                    </div>
                  }
                  // a picture shown from another language: the upload gives this language its own
                  uploadLabel={shownIn !== language ? t("Upload for %s", String(language).toUpperCase()) : undefined}
                  data-testid={`upload-${field}`}
                />
                {/* a picture is uploaded into the dialog's language; until it has one there, another language's shows */}
                <div className="flex items-center gap-1.5 text-xs text-admin_text_secondary" data-testid={`image-language-${field}`}>
                  <LanguageTag language={language} />
                  {shownIn !== language && <span>{t("No picture in %s yet: showing the %s one", String(language).toUpperCase(), String(shownIn).toUpperCase())}</span>}
                </div>
                {selected && isProductEditor && (
                  <Button variant="admin_text" onClick={() => set(field, "")} disabled={!isSpecified(field)} className="self-start text-sm">
                    <TrashIcon className="w-4 h-4" /> {t`Remove`}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {subTab === "screenshots" && showScreenshots && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MediaField
              preview={false}
              uploadLabel={t`Add Screenshot`}
              name={`${pid}-screenshot`}
              disabled={!isProductEditor}
              busy={uploading}
              upload={(file) => UploadLocalizedImage(file, { ownerModel: "Product", owner: pid, language })}
              onUploaded={screenshotUploaded}
              data-testid="upload-screenshots"
            />
            <span className="text-sm text-admin_text_secondary">{t`Drop, paste or pick an image, crop it and it is added to the screenshots`}</span>
          </div>
          {!screenshots.length && <p className="text-sm text-admin_text_secondary my-0">{t`No screenshots uploaded yet`}</p>}
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
            {screenshots.map((screenshot, index) => (
              <div
                key={screenshot}
                draggable={isProductEditor}
                onDragStart={() => setDraggedIndex(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={() => setDraggedIndex(null)}
                className={`flex flex-col gap-1 ${isProductEditor ? "cursor-grab active:cursor-grabbing" : ""} ${draggedIndex === index ? "opacity-50" : ""}`}
              >
                <div
                  className={`relative w-full h-[150px] rounded-md overflow-hidden border cursor-zoom-in ${draggedIndex === index ? "border-admin_primary" : "border-admin_bordered"}`}
                  onClick={() => setPreviewIndex(index)}
                  title={t`View full size`}
                  data-testid={`screenshot-${index}`}
                >
                  {isProductEditor && (
                    <span className="absolute top-1 left-1 z-10 bg-white/80 rounded p-0.5 text-admin_text_secondary">
                      <Bars2Icon className="w-4 h-4" />
                    </span>
                  )}
                  <img src={urljoin(mediaRoot, screenshot)} alt={t("Screenshot %s", index + 1)} className="w-full h-full object-cover pointer-events-none" />
                </div>
                {isProductEditor && (
                  <Button variant="admin_text" className="self-start text-sm text-admin_error" onClick={(e) => { e.stopPropagation(); removeScreenshot(index) }}>
                    <TrashIcon className="w-4 h-4" /> {t`Remove`}
                  </Button>
                )}
              </div>
            ))}
          </div>
          {/* the editor sits on the modal stack (~1.05e9): the preview goes above it, as the media library's does */}
          {previewIndex !== null && screenshots[previewIndex] && (
            <Lightbox src={urljoin(mediaRoot, screenshots[previewIndex])} alt={t("Screenshot %s", previewIndex + 1)} onClose={() => setPreviewIndex(null)} zIndex={2147483000} />
          )}
        </div>
      )}

      {subTab === "hover" && showScreenshots && (
        <div className="flex flex-col gap-5" data-testid="hover-image">
          <p className="text-sm text-admin_text_secondary my-0">
            {t`The hover image is what a product card or thumbnail turns to when the shopper's pointer rests on it, in the shop's lists, rows and search results. A second view of the product (the back of a shirt, the outfit worn) works best.`}
          </p>
          <RadioGroup
            variant="admin_card"
            name={`${pid}-hover-mode`}
            value={hoverMode}
            disabled={!isProductEditor}
            onChange={(mode) => setHover({ mode })}
            options={[
              { value: "random", label: t`Random from screenshots`, description: t`One of the screenshots, picked anew each time the list is read. The default.` },
              { value: "screenshot", label: t`Specific screenshot`, description: t`Always the one screenshot chosen below.` },
              { value: "custom", label: t`Image for the hover only`, description: t`A picture uploaded for this, shown nowhere else.` },
            ]}
          />

          {hoverMode === "screenshot" && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">{t`Pick the screenshot`}</span>
              {!screenshots.length && <p className="text-sm text-admin_text_secondary my-0">{t`No screenshots uploaded yet`}</p>}
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))]">
                {screenshots.map((screenshot, index) => {
                  const chosen = hoverImage.screenshot === screenshot
                  return (
                    <button
                      key={screenshot}
                      type="button"
                      disabled={!isProductEditor}
                      onClick={() => setHover({ mode: "screenshot", screenshot })}
                      className={`relative h-[120px] rounded-md overflow-hidden border-2 ${chosen ? "border-admin_primary ring-2 ring-admin_primary/30" : "border-admin_bordered hover:border-admin_primary/50"}`}
                      title={t("Screenshot %s", index + 1)}
                      data-testid={`hover-screenshot-${index}`}
                    >
                      <img src={urljoin(mediaRoot, screenshot)} alt={t("Screenshot %s", index + 1)} className="w-full h-full object-cover" />
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {hoverMode === "custom" && (
            <div className="flex flex-col gap-2 max-w-full" style={{ width: 320 }}>
              <MediaDropzone
                selected={hoverImage.image ? urljoin(mediaRoot, hoverImage.image) : null}
                name={`${pid}-hover-image`}
                disabled={!isProductEditor}
                uploading={uploading}
                upload={(file) => UploadLocalizedImage(file, { ownerModel: "Product", owner: pid, language })}
                onUploaded={hoverUploaded}
                prompt={
                  <div className="text-center">
                    <div className="text-lg font-semibold">{t`Hover image`}</div>
                    <div className="text-xs text-admin_text_secondary">{t`any size, its own aspect`}</div>
                  </div>
                }
                data-testid="upload-hover-image"
              />
              {hoverImage.image && isProductEditor && (
                <Button variant="admin_text" onClick={() => setHover({ image: "" })} className="self-start text-sm">
                  <TrashIcon className="w-4 h-4" /> {t`Remove`}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
